'use client';

import { createContext, useContext, type ReactNode } from 'react';
import { useParams } from 'next/navigation';

/**
 * What a dispatched-message presenter receives: the message, the prompt as
 * shown, the bubble without the generic attribution (for presenters that
 * name the sender themselves), and the default rendering (bubble plus
 * generic attribution) to return for any message it does not present.
 */
export type DispatchedUserMessageProps = {
  orgSlug: string;
  messageId: string;
  prompt: string;
  bubble: ReactNode;
  fallback: ReactNode;
};

/** Renders a dispatched message; returns `fallback` for ones it skips. */
export type DispatchedUserMessageRenderer = (
  props: DispatchedUserMessageProps
) => ReactNode;

const DispatchedUserMessageContext =
  createContext<DispatchedUserMessageRenderer | null>(null);

/**
 * Lets a workspace feature present user messages another thread dispatched
 * into this one. `render` must be a stable module-level function returning
 * an element of a module-level component, so hooks stay in that component.
 */
export function DispatchedUserMessageProvider({
  render,
  children,
}: {
  render: DispatchedUserMessageRenderer;
  children: ReactNode;
}) {
  return (
    <DispatchedUserMessageContext.Provider value={render}>
      {children}
    </DispatchedUserMessageContext.Provider>
  );
}

/** A dispatched user message: the provided presenter, else the default. */
export function DispatchedUserMessage({
  messageId,
  prompt,
  bubble,
  fallback,
}: Omit<DispatchedUserMessageProps, 'orgSlug'>) {
  const render = useContext(DispatchedUserMessageContext);
  const params = useParams();
  const orgSlug = typeof params.orgSlug === 'string' ? params.orgSlug : '';
  if (!render || !orgSlug) return fallback;
  return render({ orgSlug, messageId, prompt, bubble, fallback });
}
